import { Component, effect, inject, OnInit, signal, Signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { StepperComponent } from '../../components/stepper/stepper.component';
import { HeaderComponent } from '../../components/header/header.component';
import { Router } from '@angular/router';
import { LogService } from '../../services/log.service';
import { ButtonComponent } from '../../components/button/button.component';
import TYPES from '../../constants/types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SelectListComponent } from '../../components/select-list/select-list.component';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { Feeling } from '../../models/feeling';
import { SelectListItem } from '../../types/select-list-item';
import { UserFeeling } from '../../models/user-feeling';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { FeelingService } from '../../services/feeling.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { Log } from '../../models/log';

@Component({
  selector: 'app-felt',
  imports: [
    HeaderComponent,
    StepperComponent,
    ButtonComponent,
    TranslatePipe,
    SelectListComponent,
    MatSnackBarModule,
    DialogModule
  ],
  templateUrl: './felt.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './felt.component.scss'
})
export class FeltComponent implements OnInit {
  BUTTON_TYPES:any;
  step: Signal<StepperComponent | undefined> = viewChild<StepperComponent>('stepper');
  listFeelings: WritableSignal<SelectListItem[]> = signal<SelectListItem[]>([]);

  feelingDatabaseService: FeelingService<Feeling> = inject(FeelingService);
  userFeelingDatabaseService: DatabaseService<Feeling> = inject(DATABASE_SERVICE);
  authService: AuthService = inject(AUTH_SERVICE);

  snackBar:MatSnackBar = inject(MatSnackBar);
  dialog: Dialog = inject(Dialog);
  translate: TranslateService = inject(TranslateService);

  constructor(
    private router:Router,
    private logService: LogService<Log>,
  ){
    effect( ()=> {
      this.step()?.steps()[1].nativeElement.classList.add('active');
    });

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
  }

  async ngOnInit() {
    let customFeelings: Feeling[];
    if (!localStorage.getItem('custom-feelings')){
      customFeelings = await this.feelingDatabaseService.getAllCustomFeelingsFromUser(this.authService.user()?.id || '');
      localStorage.setItem('custom-feelings', JSON.stringify(customFeelings));
    }else{
      customFeelings = JSON.parse(localStorage.getItem('custom-feelings') || '');
    }
    
    let systemFeelings: Feeling[];
    if (!localStorage.getItem('system-feelings')){
      systemFeelings = await this.feelingDatabaseService.getAllSystemFeelings();
      localStorage.setItem('system-feelings', JSON.stringify(systemFeelings));
    }else{
      systemFeelings = JSON.parse(localStorage.getItem('system-feelings') || '');
    }

    const feelings = systemFeelings.concat(customFeelings);
    const selectedFeelings: string[] | undefined = this.logService.log().feelings?.split(';');
      
    if(feelings){
      for(let feeling of feelings){
        
        let selected = false;
        if (selectedFeelings?.filter((item) => item == feeling.id).length){
          selected = true;
        }

        this.listFeelings.update(
          (current) => [
              ...current, {
              id: feeling.id,  
              label: feeling.label ? feeling.label : '',
              selected: selected,
              provider: feeling.provider ? feeling.provider : ''
            }
          ]
        );
      }
    } 
  }

  close(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'log-close-dialog.alert_title',
        message: 'log-close-dialog.alert_message'
      },
    });

    dialogRef.closed.subscribe((result:any)  => {
      if(result?.action === 'continue'){
        this.logService.canAccessMindLogRegister.set(false);
        this.router.navigate(['/home'], { replaceUrl: true });  
      }
    });   
  }

  next(){
    const log = this.logService.log();
    let feelings = ''; 
    for(const item of this.listFeelings()){
      if(item.id && item.selected === true){
        feelings += item.id + ';'
      }
    }
    feelings = feelings.substring(0, feelings.length-1);
    this.logService.log.update( 
      (current) => {
        return {...current, feelings}
      }
    );
    
    // this.router.navigate(['/body-reaction'], { replaceUrl: true });
    this.router.navigate(['/body-reaction']);
  }

  back(){
    // this.router.navigate(['/mood'], { replaceUrl: true });
    history.back();
  }

  async addNewFeeling(param:SelectListItem){
    const newFeeling: Partial<Feeling> = {
      label: param.label,
      provider: param.provider
    }

    let feeling: Feeling = await this.feelingDatabaseService.getByFeelingLabel(param.label);

    if(!feeling){
      feeling = await this.feelingDatabaseService.create(
        TYPES.DATABASE.TABLES.FEELINGS, newFeeling
      );
    }

    const customFeelings: Feeling[] = JSON.parse(localStorage.getItem('custom-feelings') || '');
    
    const feelingFound = customFeelings.find(
      (item) => {
        return item.label === param.label
      }
    )

    if(!feelingFound){
      customFeelings.push(feeling);
      localStorage.setItem('custom-feelings', JSON.stringify(customFeelings));

      const userFeeling: Partial<UserFeeling> = {
        user_id: this.authService.user()?.id,
        feeling_id: feeling.id
      }

      this.userFeelingDatabaseService.create(TYPES.DATABASE.TABLES.USERS_FEELINGS, userFeeling);

      this.listFeelings.update(
        (current) => [
            ...current, {
            id: feeling.id,  
            label: feeling.label ? feeling.label : '',
            selected: true,
            provider: feeling.provider ? feeling.provider : ''
          }
        ]
      );
      const message:string = this.translate.instant('info.feeling_added');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
    }else{
      const message:string = this.translate.instant('info.feeling_exists');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
    }

  }

  async deleteFeeling(param:SelectListItem){
    await this.feelingDatabaseService.deleteFeelingFromUser(
      param.id || '',
      this.authService.user()?.id || '');
    
    const listUpdated = this.listFeelings().filter(
      (item) => {
        return item.id != param.id;
      }
    );

    this.listFeelings.set(listUpdated);

    const message:string = this.translate.instant('info.feeling_deleted');
    const action:string = this.translate.instant('buttons.dismiss');
    this.snackBar.open(message, action);

    const customFeelings: Feeling[] = JSON.parse(localStorage.getItem('custom-feelings') || '');
    const customFeelingsFiltered = customFeelings.filter(
      (item) => {
        return item.id != param.id
      }
    );
    localStorage.setItem('custom-feelings', JSON.stringify(customFeelingsFiltered));

  }

}
